import { keepPreviousData } from "@tanstack/react-query";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { computeIntegratedButMissingLink } from "@utils/ehrActions";
import { unwrapConnection } from "@utils/utils";
import { clsx } from "clsx";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useId, useState } from "react";

import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxSeparator,
} from "@fronterahealth/frontera-ui-components/ui/combobox";
import { Field, FieldError, FieldLabel } from "@fronterahealth/frontera-ui-components/ui/field";

import { useGetProviderLearnersQuery } from "@api/graphql/types-and-hooks";
import { computeIsEhrScraperUser } from "@components/ClinicalNewReportDialog/ClinicalNewReportDialog";
import { useAdminData } from "@providers/AdminDataProvider";

interface LearnerNode {
  id: string;
  externalId?: string | null;
  learnermetadata?: { firstName?: string | null; lastName?: string | null } | null;
}

const fullName = (learner: LearnerNode) =>
  [learner.learnermetadata?.firstName, learner.learnermetadata?.lastName].filter(Boolean).join(" ").trim() || "";

// Case-insensitive compare that sorts empty values last.
const compareEmptyLast = (a: string, b: string) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b, undefined, { sensitivity: "base" });
};

interface ClientComboboxProps {
  selectedClientId: string | null;
  createNewClientSelected: boolean;
  onSelect: (val: string | null) => void;
  onCreateNew: () => void;
  onIntegratedButMissingLinkChange: (value: boolean) => void;
  className?: string;
}

// Async-search client picker. Owns its search term and queries learners directly; typing is
// debounced into the query and matching raw nodes populate the list.
export const ClientCombobox: React.FC<ClientComboboxProps> = ({
  selectedClientId,
  createNewClientSelected,
  onSelect,
  onCreateNew,
  onIntegratedButMissingLinkChange,
  className,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [learnerName, setLearnerName] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [selectedLearnerSnapshot, setSelectedLearnerSnapshot] = useState<LearnerNode | null>(null);
  const id = useId();

  const { isHiRasmusUser, isMotivityUser, isCentralReachUser, externalOrganizationName, isEnsoraUser, isEHROrgUser } =
    useAdminData();
  const flags = useFlags<FeatureFlags>();

  const isEhrScraperUser = computeIsEhrScraperUser({
    isHiRasmusUser,
    isMotivityUser,
    isCentralReachUser,
    isEnsoraUser,
  });

  // Debounce the typed value into the query term; the effect cleanup cancels a pending update.
  useEffect(() => {
    const timeout = setTimeout(() => setLearnerName(inputValue), 400);
    return () => clearTimeout(timeout);
  }, [inputValue]);

  // A search is pending while the debounced term hasn't yet caught up with what's typed.
  const isSearchPending = inputValue !== learnerName;

  const { data, isFetching } = useGetProviderLearnersQuery(
    { learnerName },
    {
      placeholderData: keepPreviousData,
    },
  );

  const list = unwrapConnection(data?.getProviderLearners) ?? [];
  const learners = [...list].sort((a, b) => {
    const byFirst = compareEmptyLast(a.learnermetadata?.firstName ?? "", b.learnermetadata?.firstName ?? "");
    if (byFirst !== 0) {
      return byFirst;
    }
    return compareEmptyLast(a.learnermetadata?.lastName ?? "", b.learnermetadata?.lastName ?? "");
  });

  // Fall back to the selection-time snapshot: `learners` is name-filtered, so a new search
  // term would otherwise drop the selection and clear the gate below.
  const selectedLearnerFromList = selectedClientId
    ? learners.find((learner) => learner.id === selectedClientId)
    : undefined;
  const selectedLearner =
    selectedLearnerFromList ??
    (selectedClientId && selectedLearnerSnapshot?.id === selectedClientId ? selectedLearnerSnapshot : undefined);
  const integratedButMissingLink = computeIntegratedButMissingLink({
    hasSelectedLearner: !!selectedLearner,
    isIntegratedUser: isEhrScraperUser,
    hasExternalId: !!selectedLearner?.externalId,
    allowCreateClientWithEhr: !!flags["allow-create-client-with-ehr"],
  });

  // Lift the EHR-link status so the dialog can gate the Continue button on it.
  useEffect(() => {
    onIntegratedButMissingLinkChange(integratedButMissingLink);
  }, [integratedButMissingLink, onIntegratedButMissingLinkChange]);

  return (
    <Field data-invalid={!!integratedButMissingLink} className={className}>
      <FieldLabel htmlFor={id}>Select Client</FieldLabel>
      <Combobox<LearnerNode>
        items={learners}
        itemToStringLabel={fullName}
        isItemEqualToValue={(a, b) => a.id === b.id}
        open={open}
        onOpenChange={setOpen}
        inputValue={inputValue}
        value={
          (createNewClientSelected
            ? ({ learnermetadata: { firstName: "New", lastName: "Client" }, id: "" } as LearnerNode)
            : selectedLearner) || null
        }
        onValueChange={(next) => {
          setSelectedLearnerSnapshot(next ?? null);
          onSelect(next ? next.id : null);
        }}
        onInputValueChange={(query, { reason }) => {
          if (reason === "item-press") return;
          setInputValue(query);
        }}
      >
        <ComboboxInput
          placeholder="Type to find a client"
          showClear
          id={id}
          aria-invalid={!!integratedButMissingLink}
          data-testid="client-combobox-input"
        />
        <ComboboxContent side="bottom">
          <ComboboxEmpty>{isFetching || isSearchPending ? "Searching..." : "No clients found."}</ComboboxEmpty>
          {/* Mosaic's built-in clamp depends on a Base UI `--available-height` var that
              isn't computing reliably in this dialog; pin a fixed fallback so a long
              client list scrolls instead of pushing "Create New"/Cancel/Continue
              off-screen. Tracked upstream, not fixed at the Mosaic level yet. */}
          <ComboboxList className={clsx("max-h-80", (isFetching || isSearchPending) && "animate-pulse")}>
            {(learner: LearnerNode) => (
              <ComboboxItem key={learner.id} value={learner} data-testid={`learner-${learner.id}`}>
                <span className="ph-no-capture">{fullName(learner)}</span>
              </ComboboxItem>
            )}
          </ComboboxList>
          {!isEHROrgUser && (
            <>
              <ComboboxSeparator />
              <Button
                className="w-full justify-start font-semibold text-rio-600 py-4 px-4 text-sm cursor-pointer hover:no-underline hover:text-rio-800"
                variant="link"
                type="button"
                onClick={() => {
                  setOpen(false);
                  onCreateNew();
                }}
              >
                Create New
              </Button>
            </>
          )}
        </ComboboxContent>
      </Combobox>
      {integratedButMissingLink && <FieldError>Client must first be linked to {externalOrganizationName}</FieldError>}
    </Field>
  );
};
